import React from 'react';
import {
  JAPANESE,
  KOREAN,
  THAI,
  TRADITIONAL_CHINESE,
  SIMPLIFIED_CHINESE,
} from 'app/constants/enums/Language';
import { BASE_FONT_NAMES, CORE_FONTS, FALLBACK_FONTS } from 'app/constants/Fonts';
import { fontList } from 'app/utils/text';
import { CallbackRef } from 'app/utils/react';

export interface IProps {
  children: React.ReactNode;
  className?: string;
  getRef?: CallbackRef;
  languageCode?: string;
  style?: React.CSSProperties;
  wrap?: 'span' | 'section' | 'div';
}

/**
 * Given a `languageCode`, wraps its children with a <span> that has the appropriate font-family for
 * the given language.
 */
const WithFontsForLanguage: React.FunctionComponent<IProps> = ({
  children,
  className,
  getRef,
  languageCode = '',
  style = {},
  wrap: Wrap = 'span',
}) => {
  // If there are no styles for the specified language, then fallback to the base font names.
  const fontFamilyStyles = styles[languageCode] || BASE_FONT_NAMES_STYLE;
  return (
    <Wrap
      className={className}
      children={children}
      ref={getRef}
      lang={languageCode}
      style={{ ...style, ...fontFamilyStyles }}
    />
  );
};

const makeFont = (fonts: string[]) => fontList([
  ...CORE_FONTS,
  ...fonts,
  ...FALLBACK_FONTS,
]);

// Used when the language is not found.
const BASE_FONT_NAMES_STYLE = {
  fontFamily: fontList(BASE_FONT_NAMES),
};

const styles = {
  [JAPANESE.id]: {
    fontFamily: makeFont([
      'Noto Sans JP',
      '游ゴシック Medium',
      '游ゴシック体',
      'Yu Gothic Medium',
      'YuGothic',
      'ヒラギノ角ゴ ProN',
      'Hiragino Kaku Gothic ProN',
      'メイリオ',
      'Meiryo',
      'ＭＳ Ｐゴシック',
      'MS PGothic',
    ]),
  },
  [KOREAN.id]: {
    fontFamily: makeFont([
      '맑은 고딕',
      'Malgun Gothic',
    ]),
  },
  [THAI.id]: {
    fontFamily: makeFont([
      'DB Helvethaica',
    ]),
  },
  [TRADITIONAL_CHINESE.id]: {
    fontFamily: makeFont([
      'cwTeXHei',
    ]),
  },
  [SIMPLIFIED_CHINESE.id]: {
    fontFamily: makeFont([
      'Microsoft YaHei New',
      'Microsoft Yahei',
      '微软雅黑',
      '宋体',
      'SimSun',
      'STXihei',
      '华文细黑',
    ]),
  },
};

export default WithFontsForLanguage;
